import React, { useCallback, useEffect, useMemo, useState } from 'react'

import {
  Avatar,
  Burger,
  Flex,
  Group,
  Header as MHeader,
  Menu,
  Paper,
  Text,
  Tooltip,
  Transition,
  UnstyledButton,
  createStyles,
} from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { Icon3dCubeSphere, IconChevronDown, IconLogout, IconSettings } from '@tabler/icons-react'
import Image from 'next/image'
import Link from 'next/link'
import { useTranslation } from 'react-i18next'

import { MenuHelper } from '../helpers/Menu.helper'

import { TSAContainer } from '@testsa-com/common/src/components/TSAContainer'
import { AuthHelper } from '@testsa-com/common/src/helpers/Auth.helper'
import { usePanelFilterStore } from '@testsa-com/common/src/stores/usePanelFilterStore'
import type { MenuLink, SubLink } from '@testsa-com/common/src/types/Menu.type'
import { useRouter } from 'next/router'
import { useAppStore } from '../stores/useAppStore'
import { useUserStore } from '../stores/useUserStore'
import styles from '../styles/layout/Header.module.scss'

const HEADER_HEIGHT = 60

const useStyles = createStyles(theme => ({
  user: {
    [theme.fn.smallerThan('xs')]: {
      display: 'none',
    },
  },
  userActive: {
    backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[8] : theme.white,
  },
  dropdown: {
    [theme.fn.largerThan('sm')]: {
      display: 'none',
    },
  },

  links: {
    [theme.fn.smallerThan('sm')]: {
      display: 'none',
    },
  },

  burger: {
    [theme.fn.largerThan('sm')]: {
      display: 'none',
    },
  },

  link: {
    [theme.fn.smallerThan('sm')]: {
      borderRadius: 0,
      padding: theme.spacing.md,
    },
  },

  linkActive: {
    '&, &:hover': {
      backgroundColor: theme.fn.variant({ variant: 'light', color: theme.primaryColor }).background,
      color: theme.fn.variant({ variant: 'light', color: theme.primaryColor }).color,
    },
  },
}))

type HeaderProps = {
  links: MenuLink[]
}

export function Header({ links }: HeaderProps) {
  const [opened, { toggle, close }] = useDisclosure(false)
  const { classes, cx, theme } = useStyles()
  const { t } = useTranslation('common')
  const resetAppStore = useAppStore(s => s.reset)
  const userStore = useUserStore()
  const resetPanelFilterStore = usePanelFilterStore(s => s.reset)
  const router = useRouter()

  const [active, setActive] = useState<MenuLink>()
  const [userMenuOpened, setUserMenuOpened] = useState(false)

  const env = process.env.NEXT_PUBLIC_ENV ?? 'development'
  const rootHref = useMemo(
    () =>
      userStore.me?.type === 'clientUser'
        ? `/dashboards/organisations/${userStore.me?.organization?.id}`
        : '/',
    [userStore.me],
  )

  useEffect(() => {
    setActive(MenuHelper.getActiveLink(links, router.pathname))
  }, [links, router.pathname])

  const items = useMemo(
    () =>
      links.map(l => {
        if (l.isComingSoon) {
          return (
            <Tooltip key={l.label} label={t('words.comingSoon')} withArrow>
              <Text className={styles.linkDisabled} span>
                {l.icon || ''}
                {l.label}
              </Text>
            </Tooltip>
          )
        }

        if (
          Array.isArray(l.permission)
            ? l.permission && userStore.isAuthorisedOneOf(l.permission) === false
            : l.permission && userStore.isAuthorised(l.permission) === false
        ) {
          return (
            <Tooltip
              key={l.label}
              label={t('generics.notEnoughAccess')}
              width={280}
              multiline
              withArrow
            >
              <Text className={styles.linkDisabled} span>
                {l.icon || ''}
                {l.label}
              </Text>
            </Tooltip>
          )
        }

        if (l.subLinks && l.subLinks.length > 0) {
          return (
            <Menu
              width={260}
              position="bottom-end"
              transition="pop-top-right"
              onClose={() => setUserMenuOpened(false)}
              onOpen={() => setUserMenuOpened(true)}
              key={l.label}
            >
              <Menu.Target>
                <UnstyledButton
                  className={cx(styles.link, classes.link, {
                    [classes.linkActive]:
                      typeof active !== 'undefined' &&
                      l.subLinks.find(sl =>
                        active.subLinks?.find(
                          asl =>
                            (asl as SubLink).link && (asl as SubLink).link === (sl as SubLink).link,
                        ),
                      ),
                  })}
                >
                  <Flex direction="row" justify="center" align="center">
                    {l.icon ?? ''}
                    {l.label}
                  </Flex>
                </UnstyledButton>
              </Menu.Target>
              <Menu.Dropdown>
                {l.subLinks.map((sl, index) => {
                  if (sl.type === 'menuLabel') {
                    return <Menu.Label key={sl.label}>{sl.label}</Menu.Label>
                  }

                  if (sl.type === 'divider') {
                    return <Menu.Divider key={sl.type + index} />
                  }

                  return (
                    <React.Fragment key={sl.label}>
                      {sl.permission && userStore.isAuthorised(sl.permission) === false ? (
                        <Tooltip
                          key={sl.label}
                          label={t('generics.notEnoughAccess')}
                          width={280}
                          multiline
                          withArrow
                        >
                          <Text className={styles.linkDisabled} span>
                            {sl.icon || ''}
                            {sl.label}
                          </Text>
                        </Tooltip>
                      ) : (
                        <span key={sl.label}>
                          <Link href={sl.link ?? ''}>
                            <Menu.Item icon={sl.icon}>{sl.label}</Menu.Item>
                          </Link>
                        </span>
                      )}
                    </React.Fragment>
                  )
                })}
              </Menu.Dropdown>
            </Menu>
          )
        }

        return (
          <Link
            href={l.link ?? ''}
            className={cx(styles.link, classes.link, {
              [classes.linkActive]: active?.link === l.link,
            })}
            key={l.label}
            onClick={() => {
              setActive(l)
              close()
            }}
          >
            <Flex direction="row" justify="center" align="center">
              {l.icon ?? ''}
              {l.label}
            </Flex>
          </Link>
        )
      }),
    [active, classes.link, classes.linkActive, close, cx, links, t, userStore.isAuthorised],
  )

  const mobileItems = useMemo(
    () =>
      links.map(l => {
        if (l.isComingSoon) {
          return (
            <Tooltip key={l.label} label={t('words.comingSoon')} withArrow>
              <Text className={styles.linkDisabled} span>
                {l.icon || ''}
                {l.label}
              </Text>
            </Tooltip>
          )
        }

        if (l.permission && userStore.isAuthorised(l.permission) === false) {
          return (
            <Tooltip
              key={l.label}
              label={t('generics.notEnoughAccess')}
              width={280}
              multiline
              withArrow
            >
              <Text className={styles.linkDisabled} span>
                {l.icon ?? ''}
                {l.label}
              </Text>
            </Tooltip>
          )
        }

        if (l.subLinks && l.subLinks.length > 0) {
          return l.subLinks.map(
            sl =>
              sl.type === 'link' && (
                <Link
                  href={sl.link ?? ''}
                  className={cx(styles.link, classes.link)}
                  key={sl.label}
                  onClick={() => {
                    setActive(l)
                    close()
                  }}
                >
                  {sl.icon ?? ''}
                  {sl.label}
                </Link>
              ),
          )
        }

        return (
          <Link
            href={l.link ?? ''}
            className={cx(styles.link, classes.link, {
              [classes.linkActive]: active?.link === l.link,
            })}
            key={l.label}
            onClick={() => {
              setActive(l)
              close()
            }}
          >
            {l.icon || ''}
            {l.label}
          </Link>
        )
      }),
    [active?.link, classes.link, classes.linkActive, close, cx, links, t, userStore.isAuthorised],
  )

  const onLogout = useCallback(() => {
    AuthHelper.logout()

    userStore.reset()
    resetAppStore()
    resetPanelFilterStore()
  }, [userStore.reset, resetAppStore, resetPanelFilterStore])

  return (
    <MHeader height={HEADER_HEIGHT} mb="xl" className={styles.root}>
      <TSAContainer className={styles.header} size="xl">
        <div className={styles.logo}>
          <Link href={rootHref}>
            <Image
              alt="TEST logo"
              src={`/assets/${env}/logo.png`}
              fill
              sizes="100vw"
              style={{
                objectFit: 'contain',
              }}
            />
          </Link>
        </div>
        <Group>
          {userStore.me?.type === 'user' && (
            <Group spacing={5} className={classes.links}>
              {items}
            </Group>
          )}

          <Burger opened={opened} onClick={toggle} className={classes.burger} size="sm" />

          <Menu
            width={260}
            position="bottom-end"
            transition="pop-top-right"
            onClose={() => setUserMenuOpened(false)}
            onOpen={() => setUserMenuOpened(true)}
          >
            <Menu.Target>
              <UnstyledButton
                className={cx(styles.user, classes.user, {
                  [classes.userActive]: userMenuOpened,
                })}
              >
                <Group spacing={7}>
                  <Avatar radius="xl" size={25} />
                  <Text weight={500} size="sm" sx={{ lineHeight: 1 }} mr={3}>
                    {userStore.me?.firstName}
                    &nbsp;
                    {userStore.me?.lastName}
                  </Text>
                  <IconChevronDown size={12} stroke={1.5} />
                </Group>
              </UnstyledButton>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Label>{t('words.settings')}</Menu.Label>
              <Link href="/account">
                <Menu.Item icon={<IconSettings size={14} stroke={1.5} />}>
                  {t('account._title', { ns: 'pages' })}
                </Menu.Item>
              </Link>
              {userStore.me?.type === 'user' && (
                <Link href="/resources">
                  <Menu.Item icon={<Icon3dCubeSphere size={14} stroke={1.5} />}>
                    {t('words.resource_other')}
                  </Menu.Item>
                </Link>
              )}
              <Menu.Divider />
              <Menu.Item
                icon={<IconLogout size={14} stroke={1.5} color={theme.colors.red[6]} />}
                onClick={onLogout}
              >
                {t('words.logout')}
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>
        <Transition transition="pop-top-right" duration={200} mounted={opened}>
          {trStyles => (
            <Paper className={cx(styles.dropdown, classes.dropdown)} withBorder style={trStyles}>
              {mobileItems}
              <Link href="/account" className={cx(styles.link, classes.link)}>
                <IconSettings {...MenuHelper.ICON_PROPS} />
                {t('account._title', { ns: 'pages' })}
              </Link>
              <Link
                href=""
                className={cx(styles.link, classes.link)}
                onClick={() => AuthHelper.logout()}
              >
                <IconLogout color={theme.colors.red[6]} {...MenuHelper.ICON_PROPS} />
                {t('words.logout')}
              </Link>
            </Paper>
          )}
        </Transition>
      </TSAContainer>
    </MHeader>
  )
}
